/**
 * CORE-1736 — в адресе WebSocket хаба вместо JWT ходит билет.
 *
 * Браузер не умеет ставить заголовок на WebSocket, поэтому SignalR кладёт то,
 * что вернула `accessTokenFactory`, в `?access_token=`. CORE-1364 убрал это из
 * логов SignalR (`hubLogger`), но при обрыве соединения Chrome сам печатает
 * «WebSocket connection to '…access_token=eyJ…' failed» — и эту строку из кода
 * страницы не скрыть. Поэтому фабрика отдаёт не JWT, а билет сервера
 * (`POST /api/auth/hub-ticket`): непрозрачная строка без `eyJ`, живёт минуту,
 * хабы основного API и чат-сервиса раскрывают её обратно в JWT.
 *
 * SignalR зовёт фабрику и на negotiate, и на апгрейд, и на каждое
 * переподключение — билет на одно подключение берётся один раз и держится,
 * пока у него есть запас по сроку.
 *
 * Старый бэкенд без эндпоинта (404/405) — тогда прежний JWT, чтобы хабы не
 * отвалились на стенде, где фронт обогнал бэк. На сетевой сбой — НЕ JWT:
 * WebSocket падает именно на плохой сети, и именно тогда браузер печатает
 * адрес; SignalR повторит подключение сам.
 */
import { API_BASE_URL } from '../services/apiUrl';
import { resolveApiUrl } from '../services/apiFallback';
import { NET_RESILIENCE_ENABLED } from '../services/netResilience';
import { parseRetryAfterMs } from './rateLimitRetry';

type TokenSource = () => string | null | undefined | Promise<string | null | undefined>;
type FetchLike = (url: string, init: RequestInit) => Promise<Pick<Response, 'ok' | 'status' | 'json'> & { headers?: Pick<Headers, 'get'> }>;

/** Запас до конца срока, после которого билет уже не отдаём. */
const SAFETY_MS = 15_000;

let cached: { jwt: string; ticket: string; usableUntil: number } | null = null;

/** Для тестов: забыть выданный билет. */
export function resetHubTicketCache(): void {
  cached = null;
}

export function withHubTicket(
  getToken: TokenSource,
  fetchImpl: FetchLike = (url, init) => fetch(url, init),
  now: () => number = Date.now,
): () => Promise<string> {
  return async () => {
    const jwt = (await getToken()) ?? '';
    if (!jwt) return '';
    if (cached && cached.jwt === jwt && now() < cached.usableUntil) return cached.ticket;

    const base = `${API_BASE_URL}/api/auth/hub-ticket`;
    const url = NET_RESILIENCE_ENABLED ? resolveApiUrl(base) : base;
    const res = await fetchImpl(url, { method: 'POST', headers: { Authorization: `Bearer ${jwt}` } });
    if (res.status === 404 || res.status === 405) return jwt;
    if (!res.ok) {
      // CORE-1901 — статус и `Retry-After` едут на ошибке: 429 не сбой, а
      // просьба подождать, и пересборка хаба не должна вернуться раньше срока.
      const err: Error & { status?: number; retryAfterMs?: number } = new Error(`hub ticket: HTTP ${res.status}`);
      err.status = res.status;
      if (res.status === 429) {
        const retryAfterMs = parseRetryAfterMs(res.headers?.get?.('Retry-After'));
        if (retryAfterMs != null) err.retryAfterMs = retryAfterMs;
      }
      throw err;
    }

    const body: any = await res.json();
    const ticket = body?.ticket ?? body?.Ticket;
    if (typeof ticket !== 'string' || !ticket) throw new Error('hub ticket: empty response');
    const ttlMs = Number(body?.expiresInSeconds ?? body?.ExpiresInSeconds ?? 60) * 1000;
    cached = { jwt, ticket, usableUntil: now() + Math.max(0, ttlMs - SAFETY_MS) };
    return ticket;
  };
}
